import { getDiscountedPrice } from '../../../search/algolia/customer-pricing';

/**
 * Price rules for ProductCardTypeA (PLP), shared with the collection price sort
 * so the sort key is always the price the card shows. Prices are in dollars.
 */

/**
 * Variants grouped by first option (color), keeping only the colors the card
 * offers: in stock, tagged for back-in-stock, or every color when the whole
 * product is sold out. Each group is priced off its first variant. Second Turn
 * prices vary by condition, so those groups price off a variant that can be bought.
 *
 * @returns {Object<string, {items: Object[], price: number, comparePrice: number}>}
 */
export function groupCardColors(product, { isSecondTurn = false } = {}) {
  const { variants, tags, available } = product;

  const groups = variants.reduce((set, variant) => {
    const property = variant.option1;
    const setItem = (set[property] = set[property] || { items: [] });
    setItem.items.push(variant);
    if (variant.available) setItem.items.available = true;
    return set;
  }, {});

  Object.entries(groups).forEach(([key, value]) => {
    const isAvailable = value.items.available;
    const bisData = tags.filter((tag) => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

    if (isAvailable || bisData.length || !available) {
      const priceVariant = (isSecondTurn && value.items.find((variant) => variant.available)) || value.items[0];
      value.price = Number(priceVariant.price / 100);
      value.comparePrice = Number(priceVariant.compare_at_price / 100);
    } else {
      delete groups[key];
    }
  });

  return groups;
}

/**
 * The color a card opens on: the requested swatch, else the first offered color.
 * Cards only track a color when the first option is Color.
 */
export function getInitialCardColor(product, colorGroups, selectedColorValue) {
  if (product.options[0].name !== 'Color') return null;
  return selectedColorValue || Object.keys(colorGroups)[0] || null;
}

/**
 * The card's main price. Always prices off the selected color. Falling back to
 * product.price would use the product-wide min price, which can belong to a
 * different (often sold out) color.
 */
export function getCardPrice(product, activeColorData, customerDiscountValue) {
  if (activeColorData) {
    return getDiscountedPrice(activeColorData.price, activeColorData.comparePrice, customerDiscountValue);
  }

  return (product.price / 100) * (1 - customerDiscountValue);
}

export function getVariantDiscountLevels(product, customerDiscountValue) {
  const availableVariants = product.variants.filter((variant) => variant.available);
  const variants = availableVariants.length ? availableVariants : product.variants;

  return variants.map((variant) => {
    const variantPrice = variant.price / 100;
    const variantComparePrice = variant.compare_at_price / 100;
    const displayComparePrice = variantComparePrice > 0 ? variantComparePrice : variantPrice;
    const displayPrice = getDiscountedPrice(variantPrice, variantComparePrice, customerDiscountValue);
    const percentage = displayComparePrice > 0 && displayPrice < displayComparePrice
      ? ((displayComparePrice - displayPrice) / displayComparePrice) * 100
      : 0;

    return {
      price: Math.round(displayPrice * 100),
      percentage: Math.round(percentage),
    };
  });
}

export function getDisplayPriceRange(discountLevels) {
  const prices = discountLevels.map((level) => level.price / 100);

  if (!prices.length) return { min: 0, max: 0 };

  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/**
 * Some variants discounted and some not: the card shows "min - max" instead of one price.
 */
export function showsMixedPriceRange(discountLevels, { showDiscountBadge, isSecondTurn }) {
  const hasMixedDiscounts =
    discountLevels.some(({ percentage }) => percentage > 0) &&
    discountLevels.some(({ percentage }) => percentage === 0);
  const range = getDisplayPriceRange(discountLevels);

  return Boolean(showDiscountBadge && !isSecondTurn && hasMixedDiscounts && range.min < range.max);
}

/**
 * The leading price a PLP card renders before any swatch click: the low end of
 * a mixed range, otherwise the selected color's price.
 */
export function getCardDisplayPrice(product, { selectedColorValue, customerDiscountValue, showDiscountBadge, isSecondTurn }) {
  const discountLevels = getVariantDiscountLevels(product, customerDiscountValue);

  if (showsMixedPriceRange(discountLevels, { showDiscountBadge, isSecondTurn })) {
    return getDisplayPriceRange(discountLevels).min;
  }

  const colorGroups = groupCardColors(product, { isSecondTurn });
  const selectedColor = getInitialCardColor(product, colorGroups, selectedColorValue);

  return getCardPrice(product, selectedColor && colorGroups[selectedColor], customerDiscountValue);
}
